import { ApiService } from '../ApiService';
import type { ApiResponse } from '../types';
import type { Notification } from '../../../types/notifications';

export interface CreateNotificationRequest {
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  link?: string;
  userId?: string; // Si non fourni, notification pour l'utilisateur actuel
}

export interface NotificationsListResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  unreadCount: number;
}

export interface NotificationPreferences {
  email: {
    journal_validation: boolean;
    report_generation: boolean;
    user_mention: boolean;
    system_alerts: boolean;
    fiscal_year_closing: boolean;
  };
  browser: {
    journal_validation: boolean;
    report_generation: boolean;
    user_mention: boolean;
    system_alerts: boolean;
    fiscal_year_closing: boolean;
  };
}

/**
 * Action groupée sur les notifications - conforme à la documentation API
 */
export interface BulkNotificationAction {
  notificationIds: string[];
  action: 'markRead' | 'markUnread' | 'delete'; // Aligné avec la doc
}

/**
 * Type de notification disponible
 */
export interface NotificationType {
  key: string;
  name: string;
  description: string;
  defaultEnabled: {
    email: boolean;
    browser: boolean;
  };
}

export const notificationsApi = {
  /**
   * Obtenir toutes les notifications de l'utilisateur
   * @see GET /notifications
   */
  getNotifications: (params?: {
    page?: number;
    pageSize?: number;
    unreadOnly?: boolean;
    type?: 'info' | 'success' | 'warning' | 'error';
    from?: string;
    to?: string;
  }): Promise<ApiResponse<NotificationsListResponse>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.unreadOnly) searchParams.append('unreadOnly', params.unreadOnly.toString());
    if (params?.type) searchParams.append('type', params.type);
    if (params?.from) searchParams.append('from', params.from);
    if (params?.to) searchParams.append('to', params.to);
    
    const query = searchParams.toString();
    return ApiService.get<NotificationsListResponse>(`/notifications${query ? `?${query}` : ''}`);
  },

  /**
   * Obtenir une notification par ID
   * @see GET /notifications/{notificationId}
   */
  getNotificationById: (notificationId: string): Promise<ApiResponse<Notification>> =>
    ApiService.get<Notification>(`/notifications/${notificationId}`),

  /**
   * Créer une nouvelle notification
   * @see POST /notifications
   */
  createNotification: (notificationData: CreateNotificationRequest): Promise<ApiResponse<Notification>> =>
    ApiService.post<Notification>('/notifications', notificationData),

  /**
   * Marquer une notification comme lue
   * @see PATCH /notifications/{notificationId}/mark-read
   */
  markAsRead: (notificationId: string): Promise<ApiResponse<{ success: boolean; data: Notification }>> =>
    ApiService.patch<{ success: boolean; data: Notification }>(`/notifications/${notificationId}/mark-read`, {}),

  /**
   * Marquer une notification comme non lue
   * @see PATCH /notifications/{notificationId}/mark-unread
   */
  markAsUnread: (notificationId: string): Promise<ApiResponse<{ success: boolean; data: Notification }>> =>
    ApiService.patch<{ success: boolean; data: Notification }>(`/notifications/${notificationId}/mark-unread`, {}),

  /**
   * Obtenir les notifications non lues
   * @see GET /notifications/unread
   */
  getUnreadNotifications: (limit?: number): Promise<ApiResponse<{ success: boolean; data: Notification[] }>> =>
    ApiService.get<{ success: boolean; data: Notification[] }>('/notifications/unread', limit ? { limit } : undefined),

  /**
   * Supprimer une notification
   * @see DELETE /notifications/{notificationId}
   */
  deleteNotification: (notificationId: string): Promise<ApiResponse<{ success: boolean; data: { message: string } }>> =>
    ApiService.delete<{ success: boolean; data: { message: string } }>(`/notifications/${notificationId}`),

  /**
   * Marquer toutes les notifications comme lues
   * @see PATCH /notifications/mark-all-read
   */
  markAllAsRead: (): Promise<ApiResponse<{ success: boolean; data: { updated: number } }>> =>
    ApiService.patch<{ success: boolean; data: { updated: number } }>('/notifications/mark-all-read', {}),

  /**
   * Supprimer toutes les notifications
   * @see DELETE /notifications/all
   */
  deleteAllNotifications: (): Promise<ApiResponse<{ success: boolean; data: { deleted: number } }>> =>
    ApiService.delete<{ success: boolean; data: { deleted: number } }>('/notifications/all'),

  /**
   * Actions groupées sur les notifications
   * @see POST /notifications/bulk-action
   */
  bulkAction: (action: BulkNotificationAction): Promise<ApiResponse<{ success: boolean; data: { updated: number } }>> =>
    ApiService.post<{ success: boolean; data: { updated: number } }>('/notifications/bulk-action', action),

  /**
   * Obtenir le nombre de notifications non lues
   * @see GET /notifications/unread-count
   */
  getUnreadCount: (): Promise<ApiResponse<{ count: number }>> =>
    ApiService.get<{ count: number }>('/notifications/unread-count'),

  /**
   * Obtenir les préférences de notification de l'utilisateur
   * @see GET /notifications/preferences
   */
  getNotificationPreferences: (): Promise<ApiResponse<NotificationPreferences>> =>
    ApiService.get<NotificationPreferences>('/notifications/preferences'),

  /**
   * Mettre à jour les préférences de notification
   * @see PUT /notifications/preferences
   */
  updateNotificationPreferences: (preferences: Partial<NotificationPreferences>): Promise<ApiResponse<NotificationPreferences>> =>
    ApiService.put<NotificationPreferences>('/notifications/preferences', preferences),

  /**
   * Obtenir les types de notifications disponibles
   * @see GET /notifications/types
   */
  getNotificationTypes: (): Promise<ApiResponse<{ types: NotificationType[] }>> =>
    ApiService.get<{ types: NotificationType[] }>('/notifications/types'),

  /**
   * Envoyer une notification de test
   * @see POST /notifications/test
   */
  sendTestNotification: (type: string): Promise<ApiResponse<Notification>> =>
    ApiService.post<Notification>('/notifications/test', { type })
};
